import React from 'react';
import PropTypes from 'prop-types';
import './CollectionManagementRights.css';
import { Col, Container, Row } from 'react-bootstrap';
import UserAutoComplete from '../../form/autocomplete/user/UserAutoComplete';
import { useTranslation } from 'react-i18next';
import CollectionManagementRight from './CollectionManagementRight';
import { ReactComponent as UserIcon } from '../../utilities/icons/avatar.svg';
import { ReactComponent as GroupIcon } from '../../utilities/icons/avatar-group.svg';
import GroupAutoComplete from '../../form/autocomplete/group/GroupAutoComplete';
import HelpDialog from '../../dialog/HelpDialog';
import ElementHeader from '../../form/ElementHeader';
import Message from '../../form/Message';

const CollectionManagementRights = ({ users = [], groups = [], protectedUsers = [], onUserChange, onGroupChange, disabled, message }) => {
  const { t } = useTranslation();
  const onlyAdministrator = users.length + groups.length <= 1;

  const selectUser = (user) => {
    if (!users.includes(user.userName)) {
      const newSelectedUsers = [ ...users, user.userName ];
      if (onUserChange) {
        onUserChange(newSelectedUsers);
      }
    }
  };

  const selectGroup = (group) => {
    if (!groups.includes(group.grpName)) {
      const newSelectedGroups = [ ...groups, group.grpName ];
      if (onGroupChange) {
        onGroupChange(newSelectedGroups);
      }
    }
  };

  const removeUser = (user) => {
    if (protectedUsers.includes(user) || onlyAdministrator) {
      return;
    }
    const userRemovedUsers = users.filter(u => u !== user);
    if (onUserChange) {
      onUserChange(userRemovedUsers);
    }
  };

  const removeGroup = (group) => {
    if (onlyAdministrator) {
      return;
    }
    const groupRemovedGroups = groups.filter(g => g !== group);
    if (onGroupChange) {
      onGroupChange(groupRemovedGroups);
    }
  };

  return (
    <Container className="collection-management-rights ps-0">
      <Row>
        <Col>
          <ElementHeader 
            label={(t('collection_management_rights_form_header'))}
            helpDialog={(
              <HelpDialog label={t('collection_management_rights_help_label')}>
                {t('collection_management_rights_help_content')}
              </HelpDialog>
            )}
          >
            {t('collection_management_rights_form_header')}
          </ElementHeader>
        </Col>
      </Row>
      <Row className="mb-2">
        <Col xs={12} md={6}>
          <ElementHeader label={t('collection_management_person_form_header')} required>
            {t('collection_management_person_form_header')}
          </ElementHeader>
          <UserAutoComplete onSelect={selectUser} disabled={disabled} />
          <ul className="collection-management-rights-list">
            {users.map((user) =>
              <li key={user}>
                <CollectionManagementRight label={user} onRemove={() => removeUser(user)} Icon={UserIcon} disabled={disabled || protectedUsers.includes(user) || onlyAdministrator} />
              </li>)}
          </ul>
        </Col>
        <Col xs={12} md={6}>
          <ElementHeader label={t('collection_management_group_form_header')}>
            {t('collection_management_group_form_header')}
          </ElementHeader>
          <GroupAutoComplete onSelect={selectGroup} disabled={disabled} />
          <ul className="collection-management-rights-list collection-management-rights-groups-list">
            {groups.map((group) => 
              <li key={group}>
                <CollectionManagementRight label={group} onRemove={() => removeGroup(group)} Icon={GroupIcon} disabled={disabled || onlyAdministrator} />
              </li>)}
          </ul>
        </Col>
      </Row>
      <Row>
        <Col>
          <Message type={message?.type}>{message?.content}</Message>
        </Col>
      </Row>
    </Container>
  );
};

CollectionManagementRights.propTypes = {
  users: PropTypes.array,
  groups: PropTypes.array,
  protectedUsers: PropTypes.array,
  onUserChange: PropTypes.func,
  onGroupChange: PropTypes.func,
  message: PropTypes.shape({
    content: PropTypes.string,
    type: PropTypes.oneOf(['light', 'neutral', 'warning'])
  })
};

export default CollectionManagementRights;
